/**
 * 65.15: the smoke cloud's one rule — a standing cloud is opaque to sight. Its own module so the sight test
 * (fineTerrain personLineOfSight) and the grenades (core/grenades.ts) share it without importing each other. Pure.
 */
import type { GameState, SmokeCloud, Vec3 } from '../types';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { SMOKE_SIGHT } from '../constants/action';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';
import { dcos, dsin } from './dmath';
import { defaultOn } from './defaultOn';

/** Does a standing cloud cut the segment a–b (either end inside one counts: you see nothing from inside smoke, nor into it)? */
export function smokeBlocks(smokes: readonly SmokeCloud[] | undefined, a: Vec3, b: Vec3): boolean {
  if (!smokes || smokes.length === 0) return false;
  const ab = vecSub(b, a);
  const len2 = vecDot(ab, ab);
  for (const c of smokes) {
    const t = len2 > 0 ? Math.max(0, Math.min(1, vecDot(vecSub(c.pos, a), ab) / len2)) : 0;
    const near = vecAdd(a, vecScale(ab, t));
    if (vecLength(vecSub(c.pos, near)) <= c.radius) return true;
  }
  return false;
}

/**
 * 65.24 (with GameConfig.smokeSight): the length under which smoke hides nothing — arm's length (SMOKE_SIGHT.arm people),
 * so two people in one cloud see each other; 0 with the switch off (every cloud as 65.15 made it).
 */
export function smokeArm(state: Pick<GameState, 'config'>): number {
  return defaultOn(state.config, 'smokeSight') ? SMOKE_SIGHT.arm * PERSON_BODY_HEIGHT * personSetOf(state).k : 0;
}

/** The standing cloud `p` (a world point: an eye) is inside, or null (the first by id of those it is in). */
export function smokeCloudAt(smokes: readonly SmokeCloud[] | undefined, p: Vec3): SmokeCloud | null {
  let best: SmokeCloud | null = null;
  for (const c of smokes ?? []) if (vecLength(vecSub(c.pos, p)) <= c.radius && (!best || c.id < best.id)) best = c;
  return best;
}

/**
 * 65.24: where a person whose eye is in cloud `c` steps out of it — on the planet's surface, SMOKE_SIGHT.beyond people past
 * the cloud's edge, from its middle toward `toward` (a world point: the enemy its side knows of). A direction from the
 * planet's centre.
 */
export function smokeExit(state: Pick<GameState, 'config' | 'planets'>, c: SmokeCloud, toward: Vec3): Vec3 | null {
  const planet = state.planets[c.planetIndex];
  if (!planet) return null;
  const mid = vecNormalize(vecSub(c.pos, planet.position));
  const t = vecSub(vecNormalize(vecSub(toward, planet.position)), vecScale(mid, vecDot(vecNormalize(vecSub(toward, planet.position)), mid)));
  if (!(vecLength(t) > 1e-12)) return null;
  const way = vecNormalize(t);
  const arc = (c.radius + SMOKE_SIGHT.beyond * PERSON_BODY_HEIGHT * personSetOf(state).k) / planet.radius;
  return vecNormalize(vecAdd(vecScale(mid, dcos(arc)), vecScale(way, dsin(arc))));
}
